import { getClientId, getUserId } from "src/app/modules/localstorage";

export type ProductListCacheKind = "merchant-products" | "product-listings" | "product-listing-stores";

type CacheEntry<T> = {
  key: string;
  value: T;
  updatedAt: number;
  usedAt: number;
};

type CacheBucket<T> = { entries: CacheEntry<T>[] };

const CACHE_VERSION = "v1";
const CACHE_PREFIX = `product-list:${CACHE_VERSION}:`;
const MAX_ENTRIES = 10;

const canUseStorage = () => typeof window !== "undefined" && Boolean(window.localStorage);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>).sort().reduce<Record<string, unknown>>((result, key) => {
      const next = (value as Record<string, unknown>)[key];
      if (next !== undefined) result[key] = stableValue(next);
      return result;
    }, {});
  }
  return value;
};

export const buildProductListQueryKey = (value: unknown) => JSON.stringify(stableValue(value));

const getBucketKey = (kind: ProductListCacheKind) => {
  const clientId = getClientId();
  const userId = getUserId();
  if (!clientId || !userId) return null;
  return `${CACHE_PREFIX}${encodeURIComponent(clientId)}:${encodeURIComponent(userId)}:${kind}`;
};

const readBucket = <T>(kind: ProductListCacheKind): CacheBucket<T> | null => {
  if (!canUseStorage()) return null;
  const key = getBucketKey(kind);
  if (!key) return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "null") as CacheBucket<T> | null;
    return parsed?.entries && Array.isArray(parsed.entries) ? parsed : null;
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }
};

const writeBucket = <T>(kind: ProductListCacheKind, bucket: CacheBucket<T>) => {
  if (!canUseStorage()) return false;
  const key = getBucketKey(kind);
  if (!key) return false;
  try {
    window.localStorage.setItem(key, JSON.stringify(bucket));
    return true;
  } catch {
    return false;
  }
};

export const readProductListCache = <T>(kind: ProductListCacheKind, query: unknown): T | null => {
  const bucket = readBucket<T>(kind);
  if (!bucket) return null;
  const key = buildProductListQueryKey(query);
  const entry = bucket.entries.find((item) => item.key === key);
  if (!entry) return null;
  entry.usedAt = Date.now();
  writeBucket(kind, bucket);
  return entry.value;
};

export const writeProductListCache = <T>(kind: ProductListCacheKind, query: unknown, value: T) => {
  const key = buildProductListQueryKey(query);
  const bucket = readBucket<T>(kind) || { entries: [] };
  const now = Date.now();
  const entries = bucket.entries.filter((item) => item.key !== key);
  entries.push({ key, value, updatedAt: now, usedAt: now });
  entries.sort((a, b) => b.usedAt - a.usedAt);
  return writeBucket(kind, { entries: entries.slice(0, MAX_ENTRIES) });
};

export const invalidateProductListCache = (kind?: ProductListCacheKind) => {
  if (!canUseStorage()) return;
  const scopedKey = kind ? getBucketKey(kind) : null;
  if (scopedKey) {
    window.localStorage.removeItem(scopedKey);
    return;
  }

  const clientId = getClientId();
  const userId = getUserId();
  if (!clientId || !userId) return;
  const scope = `${CACHE_PREFIX}${encodeURIComponent(clientId)}:${encodeURIComponent(userId)}:`;
  for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(scope)) window.localStorage.removeItem(key);
  }
};

export const PRODUCT_LIST_STORAGE_PREFIX = CACHE_PREFIX;
